/**
 * ScreenOverlayManager - Manages fullscreen overlays (start, win, game over)
 * Extracted from GameScene.ts to improve modularity
 */

import Phaser from "phaser";
import { COLORS } from "../config";
import {
  UI_COLORS,
  OVERLAY_DIMENSIONS,
  getRainbowColors,
} from "../constants/gameConstants";

/**
 * Screen overlay state
 */
export type ScreenOverlayState = {
  startScreenContainer: Phaser.GameObjects.Container | null;
  startScreenElements: Phaser.GameObjects.GameObject[];
  originalViewportWidth: number;
  originalViewportHeight: number;
};

/**
 * Configuration for start screen
 */
export type StartScreenConfig = {
  levelName: string;
  levelNumber: number | string;
  password?: string;
  isCustomLevel: boolean;
  onStart: () => void;
};

/**
 * Configuration for game over screen
 */
export type GameOverScreenConfig = {
  viewportWidth: number;
  viewportHeight: number;
  totalWidth: number;
  onRestart: () => void;
};

/**
 * Configuration for win screen
 */
export type WinScreenConfig = {
  levelNumber: number;
  starsCollected: number;
  isLastLevel: boolean;
  onNextLevel: () => void;
  onPlayAgain: () => void;
};

/**
 * Create screen overlay manager
 */
export function createScreenOverlayManager(
  originalViewportWidth: number,
  originalViewportHeight: number
): ScreenOverlayState {
  return {
    startScreenContainer: null,
    startScreenElements: [],
    originalViewportWidth,
    originalViewportHeight,
  };
}

/**
 * Show start screen overlay
 */
export function showStartScreen(
  scene: Phaser.Scene,
  state: ScreenOverlayState,
  config: StartScreenConfig
): void {
  // Resize canvas to overlay dimensions
  scene.scale.resize(OVERLAY_DIMENSIONS.width, OVERLAY_DIMENSIONS.height);
  scene.cameras.main.setViewport(
    0,
    0,
    OVERLAY_DIMENSIONS.width,
    OVERLAY_DIMENSIONS.height
  );
  scene.cameras.main.stopFollow();
  scene.cameras.main.centerOn(
    OVERLAY_DIMENSIONS.centerX,
    OVERLAY_DIMENSIONS.centerY
  );

  // Create container for all start screen elements
  state.startScreenContainer = scene.add.container(0, 0);
  state.startScreenContainer.setScrollFactor(0);
  state.startScreenContainer.setDepth(200);

  // Add background image
  const bg = scene.add
    .image(OVERLAY_DIMENSIONS.centerX, OVERLAY_DIMENSIONS.centerY, "start-level-bg")
    .setOrigin(0.5);
  bg.setScrollFactor(0);
  state.startScreenElements.push(bg);

  // Display level info
  const levelNumLabel = config.isCustomLevel
    ? "Custom Level"
    : `WORLD ${config.levelNumber}`;
  const levelNumText = scene.add.text(
    OVERLAY_DIMENSIONS.centerX,
    300,
    levelNumLabel,
    {
      fontSize: "24px",
      color: "#000000",
      fontFamily: "Arial",
      fontStyle: "bold",
    }
  );
  levelNumText.setOrigin(0.5);
  levelNumText.setScrollFactor(0);
  state.startScreenElements.push(levelNumText);

  // Level name
  const levelNameText = scene.add.text(
    OVERLAY_DIMENSIONS.centerX,
    340,
    config.levelName,
    {
      fontSize: "36px",
      color: "#000000",
      fontFamily: "Arial",
      fontStyle: "bold",
    }
  );
  levelNameText.setOrigin(0.5);
  levelNameText.setScrollFactor(0);
  state.startScreenElements.push(levelNameText);

  // Password (only for non-custom levels)
  if (config.password && !config.isCustomLevel) {
    const passwordText = scene.add.text(
      OVERLAY_DIMENSIONS.centerX,
      390,
      `Password: ${config.password}`,
      {
        fontSize: "20px",
        color: "#666666",
        fontFamily: "Arial",
        fontStyle: "bold",
      }
    );
    passwordText.setOrigin(0.5);
    passwordText.setScrollFactor(0);
    state.startScreenElements.push(passwordText);
  }

  // Clickable region over the "Start" button in the background image
  const button = scene.add.rectangle(
    520,
    600,
    240,
    100,
    UI_COLORS.transparent,
    0
  );
  button.setInteractive({ useHandCursor: true });
  button.setScrollFactor(0);
  state.startScreenElements.push(button);

  button.on("pointerdown", () => {
    console.log("Start level button clicked!");
    config.onStart();
  });

  // Set up space key to start
  scene.input.keyboard?.once("keydown-SPACE", () => {
    config.onStart();
  });

  // Add all elements to the container
  state.startScreenElements.forEach((element) => {
    state.startScreenContainer?.add(element);
  });
}

/**
 * Hide start screen and restore game view
 */
export function hideStartScreen(
  scene: Phaser.Scene,
  state: ScreenOverlayState,
): void {
  // Resize canvas back to gameplay dimensions
  scene.scale.resize(state.originalViewportWidth, state.originalViewportHeight);
  scene.cameras.main.setViewport(
    0,
    0,
    state.originalViewportWidth,
    state.originalViewportHeight
  );

  // Destroy container (which destroys all child elements)
  state.startScreenContainer?.destroy();
  state.startScreenContainer = null;
  state.startScreenElements = [];
}

/**
 * Show game over screen overlay
 */
export function showGameOverScreen(
  scene: Phaser.Scene,
  config: GameOverScreenConfig
): void {
  const { viewportWidth, viewportHeight, totalWidth, onRestart } = config;

  // Create container for all game over elements
  const container = scene.add.container(0, 0);
  container.setScrollFactor(0);
  container.setDepth(200);

  // Dark overlay - covers full width including stats panel
  const overlay = scene.add.rectangle(
    totalWidth / 2,
    viewportHeight / 2,
    totalWidth,
    viewportHeight,
    UI_COLORS.gameOverOverlay,
    0.85
  );

  // Card background (red/brick style for game over)
  const cardWidth = Math.min(240, totalWidth - 40);
  const cardHeight = 140;
  const card = scene.add.rectangle(
    totalWidth / 2,
    viewportHeight / 2,
    cardWidth,
    cardHeight,
    UI_COLORS.brickBrown
  );
  card.setStrokeStyle(4, 0x000000);

  // Card highlight
  const cardHighlight = scene.add.rectangle(
    totalWidth / 2,
    viewportHeight / 2 - cardHeight / 2 + 6,
    cardWidth - 8,
    8,
    UI_COLORS.panelHighlight
  );

  // Card shadow
  const cardShadow = scene.add.rectangle(
    totalWidth / 2,
    viewportHeight / 2 + cardHeight / 2 - 6,
    cardWidth - 8,
    8,
    UI_COLORS.panelShadow
  );

  // "GAME OVER" text
  const deathText = scene.add.text(
    totalWidth / 2,
    viewportHeight / 2 - 30,
    "GAME OVER",
    {
      fontSize: "28px",
      color: "#ffffff",
      fontFamily: "Arial",
      fontStyle: "bold",
      stroke: "#000000",
      strokeThickness: 4,
    }
  );
  deathText.setOrigin(0.5);

  // Restart button (green pipe style)
  const button = scene.add.rectangle(
    totalWidth / 2,
    viewportHeight / 2 + 30,
    120,
    40,
    UI_COLORS.greenButton
  );
  button.setStrokeStyle(3, 0x000000);
  button.setScrollFactor(0);
  button.setDepth(201); // Higher than container (200) to ensure it's on top
  button.setInteractive({ useHandCursor: true });

  const buttonText = scene.add.text(
    totalWidth / 2,
    viewportHeight / 2 + 30,
    "RETRY",
    {
      fontSize: "20px",
      color: "#ffffff",
      fontFamily: "Arial",
      fontStyle: "bold",
      stroke: "#000000",
      strokeThickness: 3,
    }
  );
  buttonText.setOrigin(0.5);
  buttonText.setScrollFactor(0);
  buttonText.setDepth(202); // Even higher to be on top of button
  buttonText.setInteractive({ useHandCursor: true });
  buttonText.on("pointerdown", onRestart);

  // Button hover effects
  button.on("pointerover", () => {
    button.setFillStyle(UI_COLORS.greenButtonHover);
  });

  button.on("pointerout", () => {
    button.setFillStyle(UI_COLORS.greenButton);
  });

  button.on("pointerdown", onRestart);

  // Button text hover effects (to match button hover)
  buttonText.on("pointerover", () => {
    button.setFillStyle(UI_COLORS.greenButtonHover);
  });

  buttonText.on("pointerout", () => {
    button.setFillStyle(UI_COLORS.greenButton);
  });

  // Set up space key to retry
  scene.input.keyboard?.once("keydown-SPACE", onRestart);

  // Add non-interactive elements to container (button and buttonText are added directly to scene above)
  container.add([overlay, card, cardHighlight, cardShadow, deathText]);
}

/**
 * Show win screen overlay
 */
export function showWinScreen(
  scene: Phaser.Scene,
  config: WinScreenConfig
): void {
  // Resize canvas to overlay dimensions
  scene.scale.resize(OVERLAY_DIMENSIONS.width, OVERLAY_DIMENSIONS.height);
  scene.cameras.main.setViewport(
    0,
    0,
    OVERLAY_DIMENSIONS.width,
    OVERLAY_DIMENSIONS.height
  );
  scene.cameras.main.stopFollow();
  scene.cameras.main.centerOn(
    OVERLAY_DIMENSIONS.centerX,
    OVERLAY_DIMENSIONS.centerY
  );

  // Create container for all win screen elements
  const container = scene.add.container(0, 0);
  container.setScrollFactor(0);
  container.setDepth(200);

  // Add background image
  const bg = scene.add
    .image(
      OVERLAY_DIMENSIONS.centerX,
      OVERLAY_DIMENSIONS.centerY,
      "level-complete-bg"
    )
    .setOrigin(0.5);

  if (config.isLastLevel) {
    showFinalVictoryScreen(scene, config.onPlayAgain);
    return;
  }

  // Display level number
  const pointsText = scene.add.text(
    OVERLAY_DIMENSIONS.centerX,
    410,
    config.levelNumber.toString(),
    {
      fontSize: "48px",
      color: "#000000",
      fontFamily: "Arial",
      fontStyle: "bold",
    }
  );
  pointsText.setOrigin(0.5);

  // Clickable region over the "next level" button in the background image
  const button = scene.add.rectangle(
    500,
    670,
    280,
    100,
    UI_COLORS.transparent,
    0
  );
  button.setInteractive({ useHandCursor: true });

  button.on("pointerdown", () => {
    console.log("Next level button clicked!");
    config.onNextLevel();
  });

  // Set up space key to continue
  scene.input.keyboard?.once("keydown-SPACE", () => {
    config.onNextLevel();
  });

  // Add all elements to container
  container.add([bg, pointsText, button]);
}

/**
 * Show final victory screen (after last level)
 */
export function showFinalVictoryScreen(
  scene: Phaser.Scene,
  onPlayAgain: () => void
): void {
  const totalWidth = OVERLAY_DIMENSIONS.width;
  const viewportHeight = OVERLAY_DIMENSIONS.height;

  // Create container for all final victory elements
  const container = scene.add.container(0, 0);
  container.setScrollFactor(0);
  container.setDepth(200);

  // Big yellow star
  const star = drawBigStar(scene, totalWidth / 2, viewportHeight / 2 - 40, 80);
  container.add(star);

  // Rainbow "YOU WON" text
  const letters = ["Y", "O", "U", " ", "W", "O", "N"];
  const rainbowColors = getRainbowColors();
  const startX = totalWidth / 2 - 120;
  const y = viewportHeight / 2 + 80;

  letters.forEach((letter, i) => {
    const text = scene.add.text(startX + i * 40, y, letter, {
      fontSize: "48px",
      color: rainbowColors[i % rainbowColors.length],
      fontFamily: "Arial",
      fontStyle: "bold",
    });
    text.setOrigin(0.5);
    container.add(text);

    // Animate rainbow cycling
    scene.tweens.add({
      targets: text,
      y: y - 10,
      duration: 500,
      yoyo: true,
      repeat: -1,
      delay: i * 100,
      ease: "Sine.easeInOut",
    });
  });

  // Play again button
  const button = scene.add.rectangle(
    totalWidth / 2,
    viewportHeight / 2 + 150,
    160,
    40,
    UI_COLORS.playAgainButton
  );
  button.setStrokeStyle(2, UI_COLORS.playAgainButtonStroke);
  button.setInteractive({ useHandCursor: true });

  const buttonText = scene.add.text(
    totalWidth / 2,
    viewportHeight / 2 + 150,
    "Play Again",
    {
      fontSize: "20px",
      color: "#ffffff",
      fontFamily: "Arial",
    }
  );
  buttonText.setOrigin(0.5);

  button.on("pointerover", () => {
    button.setFillStyle(UI_COLORS.playAgainButtonHover);
  });

  button.on("pointerout", () => {
    button.setFillStyle(UI_COLORS.playAgainButton);
  });

  button.on("pointerdown", onPlayAgain);

  // Set up space key to play again
  scene.input.keyboard?.once("keydown-SPACE", onPlayAgain);

  // Add button elements to container
  container.add([button, buttonText]);
}

/**
 * Draw a big star graphic
 */
function drawBigStar(
  scene: Phaser.Scene,
  cx: number,
  cy: number,
  size: number
): Phaser.GameObjects.Graphics {
  const graphics = scene.add.graphics();
  const outerRadius = size;
  const innerRadius = size * 0.4;
  const points = 5;

  graphics.fillStyle(COLORS.yellow, 1);
  graphics.lineStyle(3, UI_COLORS.starOutline, 1);

  graphics.beginPath();
  for (let i = 0; i < points * 2; i++) {
    const radius = i % 2 === 0 ? outerRadius : innerRadius;
    const angle = (i * Math.PI) / points - Math.PI / 2;
    const x = cx + radius * Math.cos(angle);
    const y = cy + radius * Math.sin(angle);
    if (i === 0) {
      graphics.moveTo(x, y);
    } else {
      graphics.lineTo(x, y);
    }
  }
  graphics.closePath();
  graphics.fillPath();
  graphics.strokePath();

  // Add sparkle animation
  scene.tweens.add({
    targets: graphics,
    scaleX: 1.1,
    scaleY: 1.1,
    duration: 800,
    yoyo: true,
    repeat: -1,
    ease: "Sine.easeInOut",
  });

  return graphics;
}
